import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const SOURCE_COLOR = {
  Xero: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Aroflo: "text-gbe bg-blue-50 border-blue-200",
  SharePoint: "text-violet-800 bg-violet-50 border-violet-200",
  "Asset Register": "text-amber-800 bg-amber-50 border-amber-200",
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = params.get("q") || "";
    if (!query) { setResults([]); return; }
    setLoading(true);
    api.get(`/search`, { params: { q: query } })
      .then((r) => setResults(r.data.results || []))
      .finally(() => setLoading(false));
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q: q.trim() });
  };

  const grouped = results.reduce((acc, r) => {
    acc[r.source] = acc[r.source] || [];
    acc[r.source].push(r);
    return acc;
  }, {});

  return (
    <AppLayout title="Global Search" subtitle="Search across every GBE system and connected client platform">
      <form onSubmit={submit} className="mb-6">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="search-page-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try “Kingfisher”, “Chiller”, “Payroll”, “INV-1046”…"
            className="pl-11 h-12 text-base bg-white"
            autoFocus
          />
        </div>
      </form>

      {loading ? (
        <div className="h-32 bg-white border border-slate-200 rounded-lg shimmer" />
      ) : !params.get("q") ? (
        <div className="text-center text-sm text-slate-500 py-16">Type a query above to search across all connected systems.</div>
      ) : results.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-16">No results for "{params.get("q")}"</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([source, items]) => (
            <div key={source}>
              <div className="mb-2 flex items-center gap-2">
                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${SOURCE_COLOR[source]}`}>{source}</span>
                <span className="text-xs text-slate-500">{items.length} result{items.length > 1 ? "s" : ""}</span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                {items.map((r) => (
                  <div key={`${r.type}-${r.id}`}
                       data-testid={`search-result-${r.type}-${r.id}`}
                       onClick={() => r.url && navigate(r.url)}
                       className={`px-5 py-3 flex items-center justify-between hover:bg-slate-50/70 transition-colors ${r.url ? "cursor-pointer" : ""}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-800">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.subtitle}</div>
                    </div>
                    <div className="text-xs text-slate-500 tabular-nums">{r.meta}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
